import automationService from '../../../services/automationService'
import onboardingService from '../../../services/onboardingService'

const randomNodeId = () => `node_${Math.random().toString(36).slice(2, 11)}`

const splitName = (fullName = '') => {
  const parts = fullName.trim().split(/\s+/).filter(Boolean)
  return { first_name: parts[0] || '', last_name: parts.slice(1).join(' ') }
}

const messageNode = text => ({ id: randomNodeId(), type: 'send_message', label: 'Send Message', data: { message: text } })

const delayNode = days => ({
  id: randomNodeId(),
  type: 'delay',
  label: 'Delay',
  data: { delay: days, delayValue: days, delayUnit: 'days' },
})

export const buildLinkedinSequenceNodes = (messages = []) => {
  const [first = '', second = '', third = ''] = messages
  return [
    { id: randomNodeId(), type: 'send_connection', label: 'Send Connection Request', data: {} },
    {
      id: randomNodeId(),
      type: 'wait_for_connection',
      label: 'Wait for Acceptance',
      data: {
        maxWaitUnit: 'days',
        noBranch: [],
        yesBranch: [messageNode(first), delayNode(2), messageNode(second), delayNode(3), messageNode(third)],
      },
    },
  ]
}

const prospectLinkedinUrl = prospect => prospect?.linkedinUrl || prospect?.linkedin_url || ''

const prospectToLead = prospect => {
  const { first_name, last_name } = splitName(prospect.name)
  return {
    first_name,
    last_name,
    // Headline is preview-only: store it in `headline` and leave company/title
    // (position) empty so the leads UI shows them blurred until enrichment.
    headline: prospect.headline || prospect.role || '',
    profile_photo: prospect.avatar || '',
    linkedin_url: prospectLinkedinUrl(prospect),
  }
}

const PERSIST_CONCURRENCY = 8

export const persistProspectsAsLeads = async (prospects = []) => {
  const sendable = prospects.filter(prospect => prospectLinkedinUrl(prospect))
  const leadIds = []
  // Persist in small concurrent batches — a sequential createLead per prospect
  // made save-as-draft take minutes on a full ICP list (hundreds of leads).
  for (let i = 0; i < sendable.length; i += PERSIST_CONCURRENCY) {
    const batch = sendable.slice(i, i + PERSIST_CONCURRENCY)
    const results = await Promise.all(
      batch.map(prospect => onboardingService.createLead(prospectToLead(prospect)).catch(() => null)),
    )
    results.forEach(res => {
      if (res?.userLeadId) leadIds.push(res.userLeadId)
    })
  }
  return leadIds
}

// Representative IANA timezone for each proxy country a LinkedIn profile can be
// connected through. Drives the launch schedule so sending happens in the
// working hours of the proxy region the user picked when connecting LinkedIn.
const COUNTRY_TIMEZONES = {
  US: 'America/New_York',
  CA: 'America/Toronto',
  GB: 'Europe/London',
  DE: 'Europe/Berlin',
  ES: 'Europe/Madrid',
  IT: 'Europe/Rome',
  JP: 'Asia/Tokyo',
  BE: 'Europe/Brussels',
  NL: 'Europe/Amsterdam',
  PL: 'Europe/Warsaw',
  FR: 'Europe/Paris',
  SE: 'Europe/Stockholm',
  HK: 'Asia/Hong_Kong',
  AU: 'Australia/Sydney',
  HR: 'Europe/Zagreb',
}

const DEFAULT_TIMEZONE = 'America/New_York'

const timezoneForCountry = country => COUNTRY_TIMEZONES[(country || '').toUpperCase()] || DEFAULT_TIMEZONE

// The connected senders plus the timezone of their proxy region — used to launch
// the onboarding campaign without making the user open the schedule page.
export const getConnectedSenders = async () => {
  const res = await onboardingService.getConnectedLinkedinProfiles()
  const connected = (res?.data || []).filter(profile => profile.gologinProfileId)
  return {
    senderIds: connected.map(profile => profile.id),
    timezone: timezoneForCountry(connected[0]?.country),
  }
}

const DRAFT_WORKFLOW_SETTINGS = { delayBetweenActions: 5000, maxRetries: 3 }

const ALL_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

const buildLaunchSchedule = timezone => ({
  timezone: timezone || DEFAULT_TIMEZONE,
  timeFrom: '09:00 AM',
  timeTo: '04:00 PM',
  days: ALL_DAYS,
})

const fallbackCampaignName = () => `LinkedIn Campaign #${Date.now().toString(36)}`

// Ask the AI to name and describe the campaign from the ICP (and offer); fall
// back to a generic name/description if generation fails or returns nothing.
export const resolveCampaignDetails = async ({ icp, offer }) => {
  try {
    const res = await onboardingService.generateCampaignDetails({ icp, offer: offer || {} })
    const { name, description } = res?.data || {}
    return {
      name: name?.trim() || fallbackCampaignName(),
      description: description?.trim() || 'Created during onboarding',
    }
  } catch {
    return { name: fallbackCampaignName(), description: 'Created during onboarding' }
  }
}

export const createDraftLinkedinCampaign = async ({ name, description, nodes, userLeadIds }) => {
  const res = await automationService.createWorkflow({
    name: name || fallbackCampaignName(),
    description: description || 'Created during onboarding',
    nodes,
    userLeadIds,
    linkedinProfileIds: [],
    settings: DRAFT_WORKFLOW_SETTINGS,
  })
  if (!res?.success) throw new Error(res?.message || 'Failed to create campaign')
  return res.data.id
}

export const launchLinkedinCampaign = async ({ workflowId, senderIds, timezone }) => {
  const res = await automationService.executeWorkflow(workflowId, {
    linkedinProfileIds: senderIds,
    settings: buildLaunchSchedule(timezone),
  })
  if (!res?.success) throw new Error(res?.message || 'Failed to launch campaign')
  return res.data
}
